import { Button, PasswordInput } from "@caprikorn/dynamo-ui";
import { classNames } from "@caprikorn/dynamo-utils";
import { type FC, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

import { HelpModal } from "@components/HelpModal/HelpModal";
import { FORM_VALIDATION_MODE } from "@core/core.constants";
import { useLoginRedirect } from "@hooks/useLoginRedirect";
import { ArrowRightMd, CircleInfo, CloseMd } from "@icons";
import { ROUTES } from "@router/router.constants";
import type { PutUpdatePasswordAfterResetParams } from "@services/apiEcms/apiEcms.types";
import { usePostPublicLogin } from "@services/apiEcms/usePostPublicLogin";
import { usePutUpdatePasswordAfterReset } from "@services/apiEcms/usePutUpdatePasswordAfterReset";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./PasswordResetConfirmation.module.css";
import type { SetFirstPasswordFormData } from "./PasswordResetConfirmation.types";

export const PasswordResetConfirmation: FC = () => {
  const { t } = useCustomTranslation(["main", "common"]);
  const location = useLocation();
  const [isShowModalOpen, setIsShowModalOpen] = useState(false);
  useLoginRedirect();
  const searchParams = new URLSearchParams(location.search);
  const email = searchParams.get("email") ?? "";
  const token = searchParams.get("token") ?? "";

  const {
    control,
    getValues,
    handleSubmit,
    reset: resetForm,
  } = useForm<SetFirstPasswordFormData>({
    mode: FORM_VALIDATION_MODE,
    defaultValues: {
      newPassword: "",
      confirmNewPassword: "",
    },
  });

  const navigate = useNavigate();

  const { mutate, isPending } = usePutUpdatePasswordAfterReset(
    ({ password }) => ({
      password,
      email,
      token,
    }),
  );

  const loginMutation = usePostPublicLogin();
  const loginRedirect = useLoginRedirect();

  const onSubmit = (data: SetFirstPasswordFormData) => {
    const formData: PutUpdatePasswordAfterResetParams = {
      email,
      token,
      newPassword: data.newPassword,
    };

    mutate(formData, {
      onSuccess: () => {
        loginMutation.mutate(
          { email, password: data.newPassword },
          { onSuccess: () => loginRedirect },
        );
        resetForm();
      },
    });
  };

  return (
    <div className={classes.container}>
      <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <h2 className={classNames("font-title-xs", classes.subtitle)}>
          {t("auth.form.subtitle.confirmForm")}
        </h2>

        <Controller
          control={control}
          name="newPassword"
          render={({ field, fieldState }) => (
            <PasswordInput
              {...field}
              hasError={Boolean(fieldState.error)}
              hint={
                fieldState.error?.message &&
                t(fieldState.error.message, { ns: "common" })
              }
              label={t("auth.form.password.newPwd")}
              placeholder={t("auth.form.password.placeholder")}
            />
          )}
          rules={{
            deps: ["confirmNewPassword"],
            required: "form.error.required",
            validate: (value) => {
              if (value.length < 8) {
                return "form.error.password.minCharacter";
              }
              if (!/[a-z]/.test(value)) {
                return "form.error.password.minLowercase";
              }
              if (!/[A-Z]/.test(value)) {
                return "form.error.password.minUppercase";
              }
              if (!/[0-9]/.test(value)) {
                return "form.error.password.minDigit";
              }
              if (!/[!'#"*`@$%^&(){}[\]:;<>,.?~_+\-=§€£°|/\\]/.test(value)) {
                return "form.error.password.minSpecial";
              }
              return true;
            },
          }}
        />
        <Controller
          control={control}
          name="confirmNewPassword"
          render={({ field, fieldState }) => (
            <PasswordInput
              {...field}
              hasError={Boolean(fieldState.error)}
              hint={
                fieldState.error?.message &&
                t(fieldState.error.message, { ns: "common" })
              }
              label={t("settings.resetPassword.confirmPasswordLabel")}
              placeholder={t("settings.resetPassword.placeholder")}
            />
          )}
          rules={{
            deps: ["newPassword"],
            required: "form.error.required",
            validate: (value) =>
              value === getValues("newPassword") ||
              "form.error.password.equalPassword",
          }}
        />
        <Button
          className={classes.submitButton}
          data-testid="AuthFormTemplate-login-submit"
          endIcon={ArrowRightMd}
          isLoading={isPending}
          type="submit"
        >
          {t("auth.form.buttons.confirm")}
        </Button>
      </form>
      {location.pathname === ROUTES.LOGIN_FIRST_CONNECTION ? (
        <Button
          onClick={() => setIsShowModalOpen(true)}
          startIcon={CircleInfo}
          variant="tertiary"
        >
          {t("auth.form.buttons.help")}
        </Button>
      ) : (
        <Button
          data-testid="AuthFormTemplate-auth-login-cancel"
          onClick={() => navigate(ROUTES.LOGIN)}
          startIcon={CloseMd}
          variant="tertiary"
        >
          {t("button.cancel", { ns: "common" })}
        </Button>
      )}
      {isShowModalOpen && (
        <HelpModal
          isGroupDetailsQueryEnabled={false}
          onClose={() => setIsShowModalOpen(false)}
        />
      )}
    </div>
  );
};
